import { DownOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Drawer, Dropdown } from 'antd';
import HistoryPredictions from 'components/HistoryPredictions';
import LoginModal from 'components/LoginModal/LoginModal';
import { clearAuthorizationBase } from 'config/axiosBase';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAllSetting } from '../../service/base';
import {
  dispatchAuthChanged,
  dispatchOpenLoginModal,
  OPEN_LOGIN_MODAL_EVENT
} from '../../utils/common/authEvents';
import { CONST_SITE, LocalStorageKey } from '../../utils/common/const';
import CountDownFinal from './CountDownFinal';
import HotScheduleModal from './HotScheduleModal';

const navItems = [
  {
    key: 'prediction',
    href: '/',
    icon: '/assets/images/header/btn_dudoan.png',
    iconActive: '/assets/images/header/btn_dudoanclick.png'
  },
  {
    key: 'schedule',
    icon: '/assets/images/header/btn_lichthidau.png',
    iconActive: '/assets/images/header/btn_lichthidauclick.png'
  },
  {
    key: 'rule',
    href: '/rule',
    icon: '/assets/images/header/btn_thele.png',
    iconActive: '/assets/images/header/btn_theleclick.png'
  },
  {
    key: 'result',
    href: '/prize-structure',
    icon: '/assets/images/header/btn_ketqua.png',
    iconActive: '/assets/images/header/btn_ketquaclick.png'
  }
];

/** Viên thuốc: Xin chào + tên + avatar + chevron; Đăng xuất trong menu dropdown */
function LoggedInUserMenu({ authAccount, onLogout, size = 'md' }) {
  const [open, setOpen] = useState(false);
  const isCompact = size === 'sm';

  const greetCls = isCompact ? 'text-[10px]' : 'text-[11px]';
  const nameCls = isCompact ? 'text-[12px]' : 'text-[14px]';
  const avatarCls = isCompact ? 'h-8 w-8' : 'h-9 w-9';
  const userIconCls = isCompact ? 'text-[14px]' : 'text-[16px]';

  return (
    <Dropdown
      menu={{
        items: [{ key: 'logout', label: 'Đăng xuất' }],
        onClick: ({ key }) => {
          if (key === 'logout') onLogout();
        }
      }}
      trigger={['click']}
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      getPopupContainer={() => document.body}
    >
      <button
        type="button"
        className={[
          'flex flex-row items-center border border-[#dbe8f8] bg-white text-left',
          'rounded-full shadow-[0_2px_12px_rgba(17,73,160,0.12)]',
          'hover:border-[#c5ddf5] transition-[border-color,box-shadow] cursor-pointer min-w-0',
          isCompact ? 'gap-2 max-w-[min(260px,78vw)] px-2.5 py-1.5' : 'gap-2.5 px-3 py-2 max-w-[min(300px,36vw)] xl:max-w-[min(320px,28vw)]'
        ].join(' ')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <span className={`leading-tight text-[#64748b] ${greetCls}`}>Xin chào</span>
          <span
            className={`truncate font-bold italic leading-tight text-[#111827] ${nameCls}`}
            title={authAccount || undefined}
          >
            {authAccount || 'Đã đăng nhập'}
          </span>
        </div>
        <div
          className={`flex shrink-0 items-center justify-center rounded-full bg-[#0253e0] text-white shadow-sm ${avatarCls}`}
          aria-hidden
        >
          <UserOutlined className={userIconCls} />
        </div>
        <DownOutlined
          className={`shrink-0 text-[#475569] transition-transform duration-200 ${open ? '-rotate-180' : ''} ${isCompact ? 'text-[10px]' : 'text-[11px]'}`}
        />
      </button>
    </Dropdown>
  );
}

const HeaderMenu = () => {
  const [settings, setSettings] = useState({
    link_login: '#',
    link_register: '#',
    link_home: '#'
  });
  const location = useLocation();
  const activeNav = location.pathname;
  const mounted = useRef(false);
  const [isShowMenu, setIsShowMenu] = useState(false);
  const [isModalHistory, setIsModalHistory] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isHotScheduleOpen, setIsHotScheduleOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN));
  const [authAccount, setAuthAccount] = useState(() => localStorage.getItem('userAccount') || '');
  const [IP, setIP] = useState('');
  const [FP, setFP] = useState('');

  const syncAuthState = () => {
    setIsLoggedIn(!!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN));
    setAuthAccount(localStorage.getItem('userAccount') || '');
  };

  const handleLogout = () => {
    localStorage.removeItem(LocalStorageKey.ACCESS_TOKEN);
    localStorage.removeItem(LocalStorageKey.REFRESH_TOKEN);
    localStorage.removeItem(LocalStorageKey.USER_INFO);
    localStorage.removeItem('userAccount');
    clearAuthorizationBase();
    syncAuthState();
    dispatchAuthChanged();
  };

  useEffect(() => {
    const openLogin = () => setIsLoginModalOpen(true);
    window.addEventListener(OPEN_LOGIN_MODAL_EVENT, openLogin);
    return () => window.removeEventListener(OPEN_LOGIN_MODAL_EVENT, openLogin);
  }, []);

  useEffect(() => {
    mounted.current = true;

    const fetchData = async () => {
      try {
        const res = await getAllSetting({
          site: CONST_SITE.C168
        });
        if (mounted.current) {
          const settingsMap = {};
          res.data.forEach((item) => {
            settingsMap[item.key] = item.content || '#';
          });
          setSettings(settingsMap);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchData();

    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <div className="w-full">
      <div className="w-full">
        <div className="w-full max-w-[1200px] mx-auto px-[8px] md:px-[14px] mt-3 md:mt-6">
          <div
            className="h-[64px] md:h-[76px] rounded-[12px] border border-[#D9E7FF] bg-[linear-gradient(180deg,#F8FBFF_0%,#EAF1FF_100%)]
            px-3 md:px-1 lg:px-4 flex items-center justify-between overflow-hidden shadow-[0_2px_10px_rgba(17,73,160,0.08)]"
          >
          {/* Mobile Drawer */}
          <Drawer
            placement="left"
            onClose={() => setIsShowMenu(false)}
            open={isShowMenu}
            closeIcon={null}
            width={260}
            bodyStyle={{ padding: 0, position: 'relative' }}
            style={{
              overflow: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              background: 'linear-gradient(180deg, #FFFFFF 0%, #C5EEFF 100%)'
            }}
            className="drawer-hide-scrollbar"
          >
            <style>
              {`
                .drawer-hide-scrollbar {
                  scrollbar-width: none !important;
                  -ms-overflow-style: none !important;
                }
                .drawer-hide-scrollbar::-webkit-scrollbar {
                  display: none !important;
                  width: 0 !important;
                  background: transparent !important;
                }
              `}
            </style>
            <div className="flex items-center justify-center w-full py-6 border-b">
              <Link to="/" onClick={() => setIsModalHistory(false)}>
                <img
                  src="/assets/images/header/logo.png"
                  alt="Logo"
                  className="h-[44px] w-auto object-contain mx-auto"
                />
              </Link>
            </div>
            <button
              onClick={() => setIsShowMenu(false)}
              className="absolute top-3 right-3 z-10 text-gray hover:text-gray-400 transition-colors"
              aria-label="Đóng menu"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 32,
                lineHeight: 1,
                cursor: 'pointer'
              }}
            >
              ×
            </button>
            <div
              className="flex flex-col h-full"
              style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #C5EEFF 100%)' }}
            >
              <Link
                to={settings.link_home}
                onClick={() => {
                  setIsModalHistory(false);
                  setIsShowMenu(false);
                }}
                className="flex items-center px-5 py-4 border-b border-blue-900 hover:bg-blue-800 hover:text-yellow-300 font-bold text-xl text-[#0053DF] transition-colors"
              >
                Trang chủ
              </Link>
              <Link
                to="/"
                onClick={() => {
                  setIsModalHistory(false);
                  setIsShowMenu(false);
                }}
                className="flex items-center px-5 py-4 border-b border-blue-900 hover:bg-blue-800 hover:text-yellow-300 font-bold text-xl text-[#0053DF] transition-colors"
              >
                Dự đoán
              </Link>
              <button
                type="button"
                className="flex w-full cursor-pointer items-center border-0 bg-transparent px-5 py-4 text-left text-xl font-bold text-[#0053DF] transition-colors hover:bg-blue-800 hover:text-yellow-300"
                onClick={() => {
                  setIsModalHistory(false);
                  setIsShowMenu(false);
                  setIsHotScheduleOpen(true);
                }}
              >
                Lịch thi đấu
              </button>
              <Link
                to="/rule"
                onClick={() => {
                  setIsModalHistory(false);
                  setIsShowMenu(false);
                }}
                className="flex items-center px-5 py-4 border-b border-blue-900 hover:bg-blue-800 hover:text-yellow-300 font-bold text-xl text-[#0053DF] transition-colors"
              >
                Thể lệ
              </Link>
              <Link
                to="/prize-structure"
                onClick={(e) => {
                  if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
                    e.preventDefault();
                    dispatchOpenLoginModal();
                    setIsShowMenu(false);
                    return;
                  }
                  setIsModalHistory(false);
                  setIsShowMenu(false);
                }}
                className="flex items-center px-5 py-4 border-b border-blue-900 hover:bg-blue-800 hover:text-yellow-300 font-bold text-xl text-[#0053DF] transition-colors"
              >
                Kết quả dự đoán
              </Link>
              {!isLoggedIn ? (
                <Link
                  to={settings.link_register}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-5 py-4 border-b border-blue-900 hover:bg-blue-800 hover:text-yellow-300 font-bold text-xl text-[#0053DF] transition-colors"
                  onClick={() => {
                    setIsModalHistory(false);
                    setIsShowMenu(false);
                  }}
                >
                  Đăng ký
                </Link>
              ) : null}
            </div>
          </Drawer>

          {/* Left area: back + logo as one group */}
          <div className="flex items-center shrink-0 gap-2 md:gap-1 ">
            <Button
              type="link"
              onClick={() => setIsShowMenu(true)}
              className="md:hidden p-0 min-w-0 w-[36px] h-[36px] flex items-center justify-center text-[#0253e0]"
              aria-label="Mở menu"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-[34px] w-[34px] shrink-0 -translate-y-[3px]"
                fill="currentColor"
                aria-hidden
              >
                <circle cx="5" cy="6" r="2.25" />
                <rect x="10" y="4.25" width="12" height="3.5" rx="1.75" />
                <circle cx="5" cy="12" r="2.25" />
                <rect x="10" y="10.25" width="12" height="3.5" rx="1.75" />
                <circle cx="5" cy="18" r="2.25" />
                <rect x="10" y="16.25" width="12" height="3.5" rx="1.75" />
              </svg>
            </Button>
            <Link
              to={'/'}
              onClick={() => {
                setIsModalHistory(false);
                setIsShowMenu(false);
              }}
              className="hidden md:flex"
            >
            </Link>
            <Link to="/" onClick={() => setIsModalHistory(false)} className="hidden md:flex shrink-0">
              <img src="/assets/images/header/logo.png" alt="Logo" className="h-[24px] lg:h-[44px] w-auto object-contain" />
            </Link>
            <Link to="/" onClick={() => setIsModalHistory(false)} className="md:hidden shrink-0">
              <img src="/assets/images/header/logo.png" alt="Logo" className="h-[30px] w-auto object-contain" />
            </Link>
          </div>

          {/* Center area: desktop navigation */}
          <div className="flex-1 min-w-0 flex items-center justify-center md:justify-around">
            <div className="hidden md:flex items-center justify-around w-full max-w-[500px] xl:max-w-[640px] px-1 xl:px-2">
              {navItems.map((item) => {
                // Khi modal lịch mở, pathname không đổi (vd. vẫn /prize-structure) — chỉ highlight "Lịch thi đấu", các nút theo route tắt bản active.
                const routeMatches = Boolean(item.href && activeNav === item.href);
                const isRouteActive = routeMatches && !isHotScheduleOpen;
                const isScheduleModalOpen = item.key === 'schedule' && isHotScheduleOpen;
                const itemClass = 'rounded-full px-[2px] py-[2px]';
                const iconSrc =
                  isRouteActive || isScheduleModalOpen ? item.iconActive : item.icon;

                if (item.key === 'schedule') {
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setIsModalHistory(false);
                        setIsHotScheduleOpen(true);
                      }}
                      className={`${itemClass} border-none bg-transparent cursor-pointer`}
                      aria-label="Lịch thi đấu hot"
                    >
                      <img src={iconSrc} alt="Lịch thi đấu" className="h-[32px] xl:h-[42px] w-auto object-contain" />
                    </button>
                  );
                }

                if (item.key === 'result') {
                  return (
                    <Link
                      key={item.key}
                      to={item.href}
                      className={`${itemClass} inline-flex`}
                      onClick={(e) => {
                        if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
                          e.preventDefault();
                          dispatchOpenLoginModal();
                        }
                        setIsModalHistory(false);
                      }}
                    >
                      <img src={iconSrc} alt={item.key} className="h-[32px] xl:h-[42px] w-auto object-contain" />
                    </Link>
                  );
                }

                return (
                  <Link
                    key={item.key}
                    to={item.href}
                    className={`${itemClass} inline-flex ${item.key === 'prediction' ? 'translate-y-[1px]' : ''}`}
                    onClick={() => setIsModalHistory(false)}
                  >
                    <img src={iconSrc} alt={item.key} className="h-[32px] xl:h-[42px] w-auto object-contain" />
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right area */}
          <div className="flex items-center gap-2 shrink-0">
            {isLoggedIn ? (
              <div className="md:hidden min-w-0 shrink">
                <LoggedInUserMenu
                  authAccount={authAccount}
                  onLogout={handleLogout}
                  size="sm"
                />
              </div>
            ) : (
              <button
                type="button"
                className="md:hidden w-[118px] h-[36px] flex items-center justify-center bg-transparent border-0 p-0"
                onClick={() => {
                  setIsShowMenu(false);
                  setIsLoginModalOpen(true);
                }}
              >
                <img
                  src="/assets/images/header/login.png"
                  alt="Đăng nhập"
                  className="max-w-full max-h-full object-contain transition-all transform duration-150 ease-out hover:scale-105"
                />
              </button>
            )}
            <div className="hidden md:flex items-center justify-center gap-1 xl:gap-3">
              {isLoggedIn ? (
                <LoggedInUserMenu authAccount={authAccount} onLogout={handleLogout} size="md" />
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setIsLoginModalOpen(true)}
                    className="w-[88px] xl:w-[124px] h-[32px] xl:h-[38px] flex items-center justify-center bg-transparent border-0 p-0"
                  >
                    <img
                      src="/assets/images/header/login.png"
                      alt="Đăng nhập"
                      className="max-w-full max-h-full object-contain transition-all transform duration-150 ease-out hover:scale-105"
                    />
                  </button>
                  <Link
                    to={settings.link_register}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-[88px] xl:w-[124px] h-[32px] xl:h-[38px] flex items-center justify-center"
                  >
                    <img
                      src="/assets/images/header/register.png"
                      alt="Đăng ký"
                      className="max-w-full max-h-full object-contain transform transition-all duration-150 ease-out hover:scale-105"
                    />
                  </Link>
                </>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
      <div className="w-full max-w-[1000px] relative mt-2 lg:mt-4 mx-auto h-auto flex justify-center items-center">
        <img
          src="/assets/images/header/bannerpc.png"
          alt=""
          className="w-[68%] h-auto object-contain hidden md:flex"
        />
        <div className="w-[90%] h-auto flex-col flex justify-center items-center md:hidden">
          <img
            src="/assets/images/header/title.png"
            alt=""
            className="w-full h-auto object-contain md:hidden flex"
          />
          <img
            src="/assets/images/header/bannermb.png"
            alt=""
            className="w-full h-auto object-contain md:hidden flex"
          />
        </div>
        <div className="absolute top-2/3 left-1/2 -translate-x-1/2 -translate-y-1/2 -mt-2 md:mt-2">
          <CountDownFinal />
        </div>
      </div>

      {/* <div className="flex flex-row justify-center items-center w-full gap-2 sm:gap-4 lg:gap-6 md:py-3 py-0 px-2 -mt-2 md:mt-0">
        {navItems.map((item) => {
          const isActive = activeNav === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`text-center min-w-[68px] text-[10px] md:text-[14px] font-semibold flex items-center justify-center transition-colors duration-200 w-auto h-[50px] bg-transparent`}
            >
              <img
                src={isActive ? item.iconActive : item.icon}
                alt="Lịch sử"
                className="w-full h-full object-contain"
              />
            </Link>
          );
        })}
        <button
          onClick={() => setIsModalHistory(true)}
          className="text-center text-[12px] md:text-[14px] font-semibold text-white hover:text-white/80 transition-colors duration-200 cursor-pointer border-none flex items-center justify-center w-auto h-[50px] bg-transparent"
        >
          <img
            src="/assets/images/header/btn_lichsu.png"
            alt="Lịch sử"
            className="w-full h-full object-contain"
          />
        </button>
      </div> */}
      {/* </div> */}

      <HotScheduleModal open={isHotScheduleOpen} onClose={() => setIsHotScheduleOpen(false)} />
      <HistoryPredictions
        isModalVote={isModalHistory}
        setIsModalVote={setIsModalHistory}
        IP={IP}
        FP={FP}
      />
      <LoginModal
        open={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        registerHref={settings.link_register}
        onLoggedIn={syncAuthState}
      />
    </div>
  );
};

export default HeaderMenu;
